/**
 * Internal logical model (QueryPlan / AST).
 *
 * The plan separates the public input contract from engine compilers. It
 * represents query *intent*, not engine syntax. Compilers consume a validated
 * plan; they must not depend on the raw {@link QueryInput}.
 *
 * `path` on every node points back to the input location it came from, so
 * validation errors can pinpoint the exact input segment.
 */
import type {
    AggregationFunction,
    DateGranularity,
    FieldType,
    FilterOperator,
    SortDirection,
} from '../input/input-types';

export abstract class PlanNode {
    abstract readonly kind: string;
    constructor(readonly path: string) {}
}

export class FieldRefNode extends PlanNode {
    readonly kind = 'field-ref';
    constructor(
        path: string,
        readonly field: string,
        readonly type: FieldType,
        /**
         * Nested access path *within* `field` when it holds a JSON/object value
         * (equivalent to data-persistence `relativePath`). `null`/empty = scalar
         * field. Rendered per engine: JSON operators (SQL) or a dotted field
         * name (ES/OS).
         */
        readonly nestedPath: string[] | null = null,
    ) {
        super(path);
    }

    /**
     * Dotted name including the nested path (`profile.address.city`), used for
     * SQL column aliases, ES/OS field names and output metadata so the field's
     * output name is consistent across engines. Plain `field` when scalar.
     */
    qualifiedName(): string {
        return this.nestedPath && this.nestedPath.length > 0
            ? [this.field, ...this.nestedPath].join('.')
            : this.field;
    }
}

export class SourceNode extends PlanNode {
    readonly kind = 'source';
    constructor(
        path: string,
        readonly name: string,
        readonly schema: string | null = null,
    ) {
        super(path);
    }
}

export class ProjectionNode extends PlanNode {
    readonly kind = 'projection';
    constructor(
        path: string,
        readonly fields: FieldRefNode[],
    ) {
        super(path);
    }
}

/* ── Filters ─────────────────────────────────────────────────── */

export abstract class FilterNode extends PlanNode {}

export class FilterConditionNode extends FilterNode {
    readonly kind = 'filter-condition';
    constructor(
        path: string,
        readonly field: FieldRefNode,
        readonly operator: FilterOperator,
        readonly value: unknown,
    ) {
        super(path);
    }
}

export class LogicalFilterNode extends FilterNode {
    readonly kind = 'logical-filter';
    constructor(
        path: string,
        readonly operator: 'and' | 'or',
        readonly children: FilterNode[],
    ) {
        super(path);
    }
}

export class NotFilterNode extends FilterNode {
    readonly kind = 'not-filter';
    constructor(
        path: string,
        readonly child: FilterNode,
    ) {
        super(path);
    }
}

/* ── Aggregations & grouping ─────────────────────────────────── */

export class AggregationNode extends PlanNode {
    readonly kind = 'aggregation';
    constructor(
        path: string,
        readonly fn: AggregationFunction,
        /** `null` only for `count` without a field (COUNT(*)). */
        readonly field: FieldRefNode | null,
        /** Deterministic output name, generated once during plan creation. */
        readonly outputName: string,
    ) {
        super(path);
    }
}

export class GroupFieldNode extends PlanNode {
    readonly kind = 'group-field';
    constructor(
        path: string,
        readonly field: FieldRefNode,
        readonly granularity: DateGranularity | null,
        readonly offset: string | null,
        readonly limit: number | null,
        /** Deterministic output name for this group level. */
        readonly outputName: string,
    ) {
        super(path);
    }
}

export class GroupingNode extends PlanNode {
    readonly kind = 'grouping';
    constructor(
        path: string,
        readonly groups: GroupFieldNode[],
    ) {
        super(path);
    }
}

/* ── Having ──────────────────────────────────────────────────── */

export abstract class HavingNode extends PlanNode {}

export class HavingConditionNode extends HavingNode {
    readonly kind = 'having-condition';
    constructor(
        path: string,
        readonly aggregation: AggregationNode,
        readonly operator: FilterOperator,
        readonly value: unknown,
    ) {
        super(path);
    }
}

export class LogicalHavingNode extends HavingNode {
    readonly kind = 'logical-having';
    constructor(
        path: string,
        readonly operator: 'and' | 'or',
        readonly children: HavingNode[],
    ) {
        super(path);
    }
}

export class NotHavingNode extends HavingNode {
    readonly kind = 'not-having';
    constructor(
        path: string,
        readonly child: HavingNode,
    ) {
        super(path);
    }
}

/* ── Ordering ────────────────────────────────────────────────── */

export type OrderTarget =
    | { kind: 'field'; field: FieldRefNode }
    | { kind: 'aggregation'; aggregation: AggregationNode };

export class OrderNode extends PlanNode {
    readonly kind = 'order';
    constructor(
        path: string,
        readonly target: OrderTarget,
        readonly direction: SortDirection,
    ) {
        super(path);
    }
}

/* ── Pagination & limits ─────────────────────────────────────── */

export class PaginationNode extends PlanNode {
    readonly kind = 'pagination';
    constructor(
        path: string,
        readonly skip: number | null,
        readonly cursor: unknown[] | null,
    ) {
        super(path);
    }
}

export class LimitNode extends PlanNode {
    readonly kind = 'limit';
    constructor(
        path: string,
        readonly limit: number,
    ) {
        super(path);
    }
}

/* ── Root ────────────────────────────────────────────────────── */

export class QueryPlan extends PlanNode {
    readonly kind = 'query-plan';
    constructor(
        path: string,
        readonly source: SourceNode,
        readonly projection: ProjectionNode | null,
        readonly filter: FilterNode | null,
        readonly grouping: GroupingNode | null,
        readonly aggregations: AggregationNode[],
        readonly having: HavingNode | null,
        readonly ordering: OrderNode[],
        readonly pagination: PaginationNode,
        readonly limits: LimitNode,
    ) {
        super(path);
    }
}
